import React, { useEffect, useState } from 'react';
import Icon from './Icon';
import { CONVERSATIONS } from '../data/conversations';
import { money, usePrefersReducedMotion } from '../hooks';

// Animated phone call: transcript lines appear one by one, then the order
// ticket "prints", then it moves on to the next language.
export default function LiveCall() {
  const reduce = usePrefersReducedMotion();
  const [idx, setIdx] = useState(0);
  const [shown, setShown] = useState(0);
  const [printed, setPrinted] = useState(false);

  const convo = CONVERSATIONS[idx];
  const visible = reduce ? convo.lines.length : shown;
  const ticketVisible = reduce || printed;

  const goTo = (i) => {
    setIdx(i);
    setShown(0);
    setPrinted(false);
  };

  useEffect(() => {
    let t;
    const next = () => {
      setIdx((i) => (i + 1) % CONVERSATIONS.length);
      setShown(0);
      setPrinted(false);
    };
    if (reduce) t = setTimeout(next, 10000);
    else if (shown < convo.lines.length) t = setTimeout(() => setShown((s) => s + 1), shown === 0 ? 500 : 1700);
    else if (!printed) t = setTimeout(() => setPrinted(true), 900);
    else t = setTimeout(next, 4200);
    return () => clearTimeout(t);
  }, [idx, shown, printed, reduce, convo.lines.length]);

  const subtotal = convo.ticket.reduce((sum, item) => sum + item.price, 0);
  const speaking = !reduce && shown < convo.lines.length;

  return (
    <div className="call" aria-label="Sample phone order handled by the AI">
      <div className="call__head">
        <div className="call__who">
          <span className="call__avatar">
            <Icon name="phone" size={16} />
          </span>
          <div>
            <strong>Incoming call</strong>
            <span>{convo.cuisine} restaurant · pickup</span>
          </div>
        </div>
        <span className="pill pill--live">
          <span className="dot-live" /> Live
        </span>
      </div>

      <div className="call__langs" role="tablist" aria-label="Choose a language">
        {CONVERSATIONS.map((c, i) => (
          <button
            key={c.code}
            role="tab"
            aria-selected={i === idx}
            className={i === idx ? 'is-active' : ''}
            onClick={() => goTo(i)}
          >
            {c.native}
          </button>
        ))}
      </div>

      <div className="call__body">
        <div className="call__lines" aria-live="polite">
          {convo.lines.slice(0, visible).map((line, i) => (
            <div key={`${idx}-${i}`} className={`bubble bubble--${line.who}`}>
              <span className="bubble__who">{line.who === 'ai' ? 'AI host' : 'Caller'}</span>
              <p lang={convo.code}>{line.text}</p>
              {line.en && <p className="bubble__en">{line.en}</p>}
              {line.upsell && (
                <span className="bubble__tag">
                  <Icon name="sparkles" size={12} /> Upsell
                </span>
              )}
            </div>
          ))}
        </div>

        <div className={`ticket${ticketVisible ? ' ticket--in' : ''}`} aria-hidden={!ticketVisible}>
          <div className="ticket__head">
            <span>
              <Icon name="printer" size={14} /> Sent to kitchen
            </span>
            <span className="ticket__ok">
              <Icon name="check" size={14} />
            </span>
          </div>
          {convo.ticket.map((item) => (
            <div key={item.name} className="ticket__row">
              <span>
                {item.qty}× {item.name}
                {item.mod && <em> · {item.mod}</em>}
                {item.upsell && <b className="ticket__up">+AI</b>}
              </span>
              <span>{money(item.price)}</span>
            </div>
          ))}
          <div className="ticket__row ticket__total">
            <span>Subtotal</span>
            <span>{money(subtotal)}</span>
          </div>
        </div>
      </div>

      <div className={`wave${speaking ? ' wave--on' : ''}`} aria-hidden="true">
        {Array.from({ length: 32 }, (_, i) => (
          <span key={i} style={{ animationDelay: `${(i % 8) * 0.09}s` }} />
        ))}
      </div>
    </div>
  );
}
